import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, BookOpen, Server, Shield, CreditCard, Mail, Users, 
  FileText, Settings, Zap, Globe, Lock, Download, Upload, 
  ChevronDown, ChevronRight, Copy, Check, Crown, Smartphone,
  Database, Key, Bell, Palette, Search, BarChart3, HelpCircle, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDocumentation = () => {
  const [expandedSections, setExpandedSections] = useState(['intro', 'services']);
  const [copiedCode, setCopiedCode] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const { toast } = useToast();

  const toggleSection = (section) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section) 
        : [...prev, section]
    );
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const token = localStorage.getItem('sendbox_token');
      const response = await axios.get(`${API}/admin/documentation/pdf`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'SENDBOX_Documentation_Complete.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({ title: 'PDF téléchargé', description: 'La documentation a été téléchargée avec succès' });
    } catch (e) {
      toast({ title: 'Erreur', description: 'Impossible de télécharger le PDF', variant: 'destructive' });
    }
    setDownloadingPdf(false);
  };

  const CodeBlock = ({ code, id, language = 'bash' }) => (
    <div className="relative bg-slate-950 rounded-lg p-4 mt-2 mb-4 overflow-x-auto">
      <button 
        onClick={() => copyToClipboard(code, id)}
        className="absolute top-2 right-2 p-1.5 bg-slate-800 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition-colors"
      >
        {copiedCode === id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
      </button>
      <pre className="text-sm text-green-400 font-mono whitespace-pre-wrap">{code}</pre>
    </div>
  );

  const Section = ({ id, title, icon: Icon, children, color = 'blue' }) => {
    const isExpanded = expandedSections.includes(id);
    const colors = {
      blue: 'bg-blue-500/20 text-blue-400 border-blue-500/20',
      purple: 'bg-purple-500/20 text-purple-400 border-purple-500/20',
      green: 'bg-green-500/20 text-green-400 border-green-500/20',
      amber: 'bg-amber-500/20 text-amber-400 border-amber-500/20',
      cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/20',
      red: 'bg-red-500/20 text-red-400 border-red-500/20',
    };
    
    return (
      <div className={`bg-slate-800/50 border ${colors[color].split(' ')[2]} rounded-xl overflow-hidden mb-4`}>
        <button 
          onClick={() => toggleSection(id)}
          className="w-full p-5 flex items-center gap-4 hover:bg-slate-800/70 transition-colors"
        >
          <div className={`p-2.5 rounded-lg ${colors[color].split(' ').slice(0, 2).join(' ')}`}>
            <Icon className="w-5 h-5" />
          </div>
          <h2 className="text-lg font-semibold text-white flex-1 text-left">{title}</h2>
          {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
        </button>
        {isExpanded && (
          <div className="px-5 pb-5 border-t border-slate-700/50 pt-4">
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <NavigationMenu />
      
      <main className="pt-24 pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <Link to="/admin/dashboard?tab=settings" className="inline-flex items-center gap-2 text-blue-300/60 hover:text-blue-300 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Retour au tableau de bord
          </Link>

          <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-cyan-500/20 rounded-xl">
                <BookOpen className="w-8 h-8 text-cyan-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Documentation SENDBOX</h1>
                <p className="text-blue-300/60">Guide complet d'installation, configuration et utilisation</p>
              </div>
            </div>
            <Button 
              onClick={downloadPdf}
              disabled={downloadingPdf}
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
              data-testid="download-pdf-btn"
            >
              {downloadingPdf ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Télécharger le PDF complet
            </Button>
          </div>

          {/* Introduction */}
          <Section id="intro" title="Présentation de SENDBOX" icon={Globe} color="cyan">
            <div className="prose prose-invert max-w-none">
              <p className="text-blue-200/80 leading-relaxed">
                <strong className="text-white">SENDBOX</strong> est une plateforme complète de partage de fichiers sécurisé, 
                conçue pour les entreprises et les particuliers. Elle permet d'envoyer des fichiers volumineux 
                (jusqu'à 20 Go) avec chiffrement, protection par mot de passe, et de nombreuses fonctionnalités avancées.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="bg-slate-900/50 rounded-lg p-4 border border-cyan-500/10">
                  <Shield className="w-6 h-6 text-cyan-400 mb-2" />
                  <h4 className="font-semibold text-white mb-1">Sécurisé</h4>
                  <p className="text-sm text-blue-200/60">Chiffrement AES-256, SSL/TLS, protection par mot de passe</p>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4 border border-cyan-500/10">
                  <Zap className="w-6 h-6 text-amber-400 mb-2" />
                  <h4 className="font-semibold text-white mb-1">Rapide</h4>
                  <p className="text-sm text-blue-200/60">Upload optimisé par chunks, compression automatique</p>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4 border border-cyan-500/10">
                  <Users className="w-6 h-6 text-green-400 mb-2" />
                  <h4 className="font-semibold text-white mb-1">Collaboratif</h4>
                  <p className="text-sm text-blue-200/60">Partage par email, QR code, transferts P2P</p>
                </div>
              </div>
            </div>
          </Section>

          {/* Services & Fonctionnalités */}
          <Section id="services" title="Services et Fonctionnalités" icon={Server} color="purple">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-purple-400" /> Partage de fichiers
                </h3>
                <ul className="space-y-2 text-blue-200/80">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                    <span><strong>Upload jusqu'à 20 Go</strong> - Envoyez des fichiers volumineux sans limite de taille (selon votre plan)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                    <span><strong>Compression ZIP automatique</strong> - Les fichiers multiples sont automatiquement compressés</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                    <span><strong>QR Code de partage</strong> - Générez un QR code pour un partage facile</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                    <span><strong>Envoi par email intégré</strong> - Envoyez directement à un destinataire avec notification</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                    <span><strong>Lien de téléchargement</strong> - Partagez un lien unique et sécurisé</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-400" /> Sécurité
                </h3>
                <ul className="space-y-2 text-blue-200/80">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                    <span><strong>Chiffrement E2E</strong> - Chiffrement de bout en bout pour les utilisateurs Pro/Ultimate</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                    <span><strong>Protection par mot de passe</strong> - Sécurisez vos fichiers avec un mot de passe</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                    <span><strong>Authentification 2FA</strong> - Double authentification pour les comptes admin</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                    <span><strong>Expiration automatique</strong> - Les fichiers sont supprimés après la période définie</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-purple-400" /> Fonctionnalités avancées (Pro/Ultimate)
                </h3>
                <ul className="space-y-2 text-blue-200/80">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                    <span><strong>Transfert P2P (Ultimate)</strong> - Transfert direct peer-to-peer via WebRTC, sans passer par le serveur</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                    <span><strong>Notification de téléchargement</strong> - Recevez un email quand vos fichiers sont téléchargés</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                    <span><strong>Vente de fichiers</strong> - Vendez vos fichiers avec intégration Stripe</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                    <span><strong>Fond d'écran personnalisé</strong> - Personnalisez l'apparence de votre espace</span>
                  </li>
                </ul>
              </div>
            </div>
          </Section>

          {/* Formules et Tarifs */}
          <Section id="plans" title="Formules et Tarifs" icon={Crown} color="amber">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-slate-900/50 rounded-xl p-5 border border-blue-500/20">
                <h3 className="text-lg font-semibold text-white mb-2">Gratuit</h3>
                <p className="text-2xl font-bold text-white mb-4">0€</p>
                <ul className="space-y-2 text-sm text-blue-200/70">
                  <li>• Jusqu'à 2 Go par fichier</li>
                  <li>• 5 fichiers max par envoi</li>
                  <li>• Liens valides 7 jours</li>
                  <li>• 10 téléchargements max</li>
                  <li>• Compression ZIP</li>
                  <li>• QR Code de partage</li>
                  <li>• Protection mot de passe</li>
                </ul>
              </div>
              
              <div className="bg-gradient-to-br from-blue-900/50 to-purple-900/50 rounded-xl p-5 border border-blue-400/30 relative">
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-xs px-3 py-1 rounded-full">Populaire</span>
                <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-yellow-400" /> Pro
                </h3>
                <p className="text-2xl font-bold text-white mb-4">9,99€<span className="text-sm font-normal text-blue-300/60">/mois</span></p>
                <ul className="space-y-2 text-sm text-blue-200/70">
                  <li>• <strong className="text-white">Jusqu'à 20 Go</strong> par fichier</li>
                  <li>• <strong className="text-white">Fichiers illimités</strong></li>
                  <li>• Liens valides <strong className="text-white">30 jours</strong></li>
                  <li>• Téléchargements <strong className="text-white">illimités</strong></li>
                  <li>• Chiffrement E2E</li>
                  <li>• Notification de téléchargement</li>
                  <li>• Fond d'écran personnalisé</li>
                  <li>• Vente de fichiers</li>
                </ul>
              </div>
              
              <div className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 rounded-xl p-5 border border-purple-400/30 relative">
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-purple-500 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
                  <Zap className="w-3 h-3" /> Nouveau
                </span>
                <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-purple-400" /> Ultimate
                </h3>
                <p className="text-2xl font-bold text-white mb-4">14,99€<span className="text-sm font-normal text-blue-300/60">/mois</span></p>
                <ul className="space-y-2 text-sm text-blue-200/70">
                  <li>• <strong className="text-white">Tout le plan Pro</strong></li>
                  <li>• Liens valides <strong className="text-white">60 jours</strong></li>
                  <li>• <strong className="text-purple-300">Transfert P2P (WebRTC)</strong></li>
                  <li>• Transfert direct sans serveur</li>
                  <li>• Aucune limite de taille en P2P</li>
                  <li>• Support prioritaire</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <p className="text-sm text-amber-200/80">
                <strong>💡 Astuce :</strong> Les tarifs annuels offrent une réduction de 17%. 
                Configurez vos prix dans <Link to="/admin/stripe" className="text-amber-400 hover:underline">Configuration Stripe</Link>.
              </p>
            </div>
          </Section>

          {/* Installation */}
          <Section id="installation" title="Procédure d'Installation" icon={Download} color="green">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">1. Prérequis</h3>
                <ul className="space-y-2 text-blue-200/80">
                  <li>• <strong>Node.js 18+</strong> - Pour le frontend React</li>
                  <li>• <strong>Python 3.11+</strong> - Pour le backend FastAPI</li>
                  <li>• <strong>MongoDB 6+</strong> - Base de données</li>
                  <li>• <strong>Serveur SMTP</strong> - Pour l'envoi d'emails (OVH, Gmail, etc.)</li>
                  <li>• <strong>Compte Stripe</strong> - Pour les paiements (optionnel)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3">2. Installation du Backend</h3>
                <CodeBlock id="backend-install" code={`# Cloner le dépôt
git clone https://github.com/votre-repo/sendbox.git
cd sendbox/backend

# Créer l'environnement virtuel
python -m venv venv
source venv/bin/activate  # Linux/Mac
# ou: venv\\Scripts\\activate  # Windows

# Installer les dépendances
pip install -r requirements.txt

# Configurer les variables d'environnement
cp .env.example .env
# Éditer .env avec vos paramètres

# Lancer le serveur
uvicorn server:app --host 0.0.0.0 --port 8001 --reload`} />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3">3. Installation du Frontend</h3>
                <CodeBlock id="frontend-install" code={`cd frontend

# Installer les dépendances
yarn install

# Configurer l'URL du backend
echo "REACT_APP_BACKEND_URL=https://votre-domaine.com" > .env

# Lancer en développement
yarn start

# Ou construire pour la production
yarn build`} />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3">4. Configuration MongoDB</h3>
                <CodeBlock id="mongo-config" code={`# Dans backend/.env
MONGO_URL=mongodb://localhost:27017
DB_NAME=sendbox

# Ou avec authentification
MONGO_URL=mongodb://user:password@localhost:27017/sendbox?authSource=admin`} />
              </div>
            </div>
          </Section>

          {/* Configuration Admin */}
          <Section id="admin-config" title="Configuration du Backoffice" icon={Settings} color="blue">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-400" /> Configuration SMTP
                </h3>
                <p className="text-blue-200/80 mb-3">
                  Configurez votre serveur SMTP pour l'envoi d'emails (vérification, notifications, etc.)
                </p>
                <div className="bg-slate-900/50 rounded-lg p-4 space-y-2 text-sm">
                  <p><strong className="text-white">Emplacement :</strong> <Link to="/admin/smtp" className="text-blue-400 hover:underline">Admin → SMTP</Link></p>
                  <p><strong className="text-white">Paramètres requis :</strong></p>
                  <ul className="ml-4 text-blue-200/70">
                    <li>• Serveur SMTP (ex: ssl0.ovh.net)</li>
                    <li>• Port (465 pour SSL, 587 pour TLS)</li>
                    <li>• Email et mot de passe</li>
                    <li>• Email et nom d'expéditeur</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-purple-400" /> Configuration Stripe
                </h3>
                <p className="text-blue-200/80 mb-3">
                  Configurez Stripe pour accepter les paiements Pro et Ultimate.
                </p>
                <div className="bg-slate-900/50 rounded-lg p-4 space-y-2 text-sm">
                  <p><strong className="text-white">Emplacement :</strong> <Link to="/admin/stripe" className="text-blue-400 hover:underline">Admin → Stripe</Link></p>
                  <p><strong className="text-white">Paramètres requis :</strong></p>
                  <ul className="ml-4 text-blue-200/70">
                    <li>• Clé secrète (sk_live_... ou sk_test_...)</li>
                    <li>• Clé publique (pk_live_... ou pk_test_...)</li>
                    <li>• Webhook secret (optionnel)</li>
                    <li>• Prix mensuel/annuel Pro et Ultimate</li>
                  </ul>
                  <p className="mt-2 text-amber-300/80">
                    💡 Obtenez vos clés sur <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline">dashboard.stripe.com</a>
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-green-400" /> Limites des Plans
                </h3>
                <p className="text-blue-200/80 mb-3">
                  Configurez les limites pour chaque plan (Gratuit, Pro, Ultimate).
                </p>
                <div className="bg-slate-900/50 rounded-lg p-4 space-y-2 text-sm">
                  <p><strong className="text-white">Emplacement :</strong> <Link to="/admin" className="text-blue-400 hover:underline">Admin → Limites & Paramètres</Link></p>
                  <p><strong className="text-white">Options configurables :</strong></p>
                  <ul className="ml-4 text-blue-200/70">
                    <li>• Jours d'expiration des liens</li>
                    <li>• Nombre max de téléchargements</li>
                    <li>• Nombre max de fichiers par envoi</li>
                    <li>• Uploads max par heure (gratuit)</li>
                    <li>• Protection mot de passe (gratuit)</li>
                  </ul>
                  <p className="mt-2 text-blue-300/80">
                    <strong>Note :</strong> 0 = illimité pour les plans Pro/Ultimate
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <Palette className="w-5 h-5 text-pink-400" /> Personnalisation
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-slate-900/50 rounded-lg p-4 text-sm">
                    <p className="font-semibold text-white mb-2">Thèmes</p>
                    <p className="text-blue-200/70">Personnalisez les couleurs et l'apparence</p>
                    <Link to="/admin/themes" className="text-blue-400 hover:underline text-xs mt-2 block">Admin → Thèmes</Link>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4 text-sm">
                    <p className="font-semibold text-white mb-2">Templates Email</p>
                    <p className="text-blue-200/70">Personnalisez les emails envoyés</p>
                    <Link to="/admin/email-templates" className="text-blue-400 hover:underline text-xs mt-2 block">Admin → Templates Email</Link>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4 text-sm">
                    <p className="font-semibold text-white mb-2">Pages CMS</p>
                    <p className="text-blue-200/70">Éditez les pages du site</p>
                    <Link to="/admin/dashboard?tab=pages" className="text-blue-400 hover:underline text-xs mt-2 block">Admin → Pages</Link>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4 text-sm">
                    <p className="font-semibold text-white mb-2">SEO</p>
                    <p className="text-blue-200/70">Optimisez le référencement</p>
                    <Link to="/admin/seo" className="text-blue-400 hover:underline text-xs mt-2 block">Admin → SEO</Link>
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* Extensions */}
          <Section id="extensions" title="Extensions et Intégrations" icon={Smartphone} color="cyan">
            <div className="space-y-4">
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-400" /> Extension Outlook
                </h3>
                <p className="text-sm text-blue-200/70 mb-2">
                  Envoyez des fichiers directement depuis Outlook via SENDBOX.
                </p>
                <Link to="/admin/extensions" className="text-blue-400 hover:underline text-sm">Configurer → Admin → Extensions</Link>
              </div>
              
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-red-400" /> Extension Gmail
                </h3>
                <p className="text-sm text-blue-200/70 mb-2">
                  Intégrez SENDBOX à Gmail pour un envoi simplifié.
                </p>
                <Link to="/admin/extensions" className="text-blue-400 hover:underline text-sm">Configurer → Admin → Extensions</Link>
              </div>
              
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-green-400" /> Application Mobile
                </h3>
                <p className="text-sm text-blue-200/70 mb-2">
                  Application mobile disponible sur iOS et Android (PWA).
                </p>
                <Link to="/mobile" className="text-blue-400 hover:underline text-sm">Voir → /mobile</Link>
              </div>
              
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <Database className="w-4 h-4 text-purple-400" /> Application Desktop (Electron)
                </h3>
                <p className="text-sm text-blue-200/70 mb-2">
                  Application de bureau pour Windows, Mac et Linux.
                </p>
                <p className="text-xs text-blue-300/50">Téléchargement disponible sur la page d'accueil</p>
              </div>
            </div>
          </Section>

          {/* Support */}
          <Section id="support" title="Support et Aide" icon={HelpCircle} color="red">
            <div className="space-y-4">
              <p className="text-blue-200/80">
                Pour toute question ou problème technique, plusieurs options s'offrent à vous :
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <h3 className="font-semibold text-white mb-2">Tickets de Support</h3>
                  <p className="text-sm text-blue-200/70 mb-2">
                    Ouvrez un ticket depuis la page Support du site.
                  </p>
                  <Link to="/support" className="text-blue-400 hover:underline text-sm">Accéder au Support</Link>
                </div>
                
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <h3 className="font-semibold text-white mb-2">Gestion des Tickets</h3>
                  <p className="text-sm text-blue-200/70 mb-2">
                    Gérez les tickets depuis le backoffice admin.
                  </p>
                  <Link to="/admin/dashboard?tab=tickets" className="text-blue-400 hover:underline text-sm">Admin → Tickets</Link>
                </div>
                
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <h3 className="font-semibold text-white mb-2">FAQ</h3>
                  <p className="text-sm text-blue-200/70 mb-2">
                    Consultez les questions fréquentes.
                  </p>
                  <Link to="/faq" className="text-blue-400 hover:underline text-sm">Voir la FAQ</Link>
                </div>
                
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <h3 className="font-semibold text-white mb-2">Contact</h3>
                  <p className="text-sm text-blue-200/70 mb-2">
                    Contactez-nous directement par email.
                  </p>
                  <Link to="/contact" className="text-blue-400 hover:underline text-sm">Page Contact</Link>
                </div>
              </div>
            </div>
          </Section>

        </div>
      </main>
      
      <DynamicFooter fixed={true} />
    </div>
  );
};

export default AdminDocumentation;
